"use client";

import { useQuery } from "@tanstack/react-query";

import { marketDataApi, type AssetDto, type AssetsListResponse } from "@/lib/api";
import { useTenant } from "@/hooks/useTenant";

const FIVE_MINUTES = 5 * 60 * 1000;

/**
 * SFWL-004: Per-tenant tradeable asset universe.
 *
 * Backed by `GET /api/market-data/assets`, which filters by the active
 * tenant's `marketScope` (resolved server-side from the `x-tenant-id`
 * header set by Next.js middleware). The React Query cache key includes
 * the tenant id so swapping subdomains in the same browser flushes the
 * old tenant's universe and refetches.
 */
export function useAssets() {
  const tenant = useTenant();
  const query = useQuery<AssetsListResponse>({
    queryKey: ["assets", tenant.id ?? "default", tenant.marketScope],
    queryFn: () => marketDataApi.listAssets(),
    staleTime: FIVE_MINUTES,
  });

  const assets: AssetDto[] = query.data?.assets ?? [];

  return {
    assets,
    scope: query.data?.scope ?? tenant.marketScope,
    isLoading: query.isPending,
    error: query.error as Error | null,
  };
}
